export const solutions = {
  'oems': {
    title: 'Mushin for OEMs',
    subtitle: 'Audit-ready plant control across your manufacturing ecosystem',
    description: 'OEMs face the highest bar of operational discipline. Every customer audit, every compliance expectation, every traceability demand lands on the plant floor. Mushin gives OEMs a digital backbone for execution, evidence, and governance.',
    challenges: [
      'Customer audit readiness across multiple product lines',
      'Multi-plant standardization of processes and workflows',
      'Traceability pressure from downstream customers',
      'Evidence retrieval under audit timelines',
      'Supplier quality visibility across the vendor ecosystem',
      'Execution control at scale across shifts and programs',
    ],
    products: ['Mushin Factory OS', 'Mushin Vendor Connect'],
    outcomes: [
      'Audit-ready execution records available in minutes, not days',
      'Standardized workflows across plants and product lines',
      'Complete traceability from raw material to dispatch',
      'Supplier quality governance through Mushin Vendor Connect',
      'Machine and process visibility through IoT & Machine Data',
      'ERP integration as a service for SAP and Oracle linkage',
      'Safety compliance and incident tracking across plants',
    ],
  },
  'tier-1': {
    title: 'Mushin for Tier 1 Manufacturers',
    subtitle: 'Scale plant control without scaling complexity',
    description: 'Tier 1 manufacturers operate under direct OEM scrutiny. IATF compliance, VDA audits, customer-specific requirements: the plant must deliver consistent execution under pressure. Mushin provides the operational discipline layer that keeps Tier 1 plants running with confidence.',
    challenges: [
      'Meeting OEM-specific audit and compliance expectations',
      'Managing quality execution across multiple customer programs',
      'Complaint containment and CAPA response speed',
      'Maintaining execution discipline during people movement',
      'Standards control and document governance',
      'Traceability for containment and recall scenarios',
    ],
    products: ['Mushin Factory OS', 'Mushin Vendor Connect'],
    outcomes: [
      'Faster audit evidence retrieval across IATF, VDA, VSA requirements',
      'Digital quality execution replacing paper-based workflows',
      'Structured complaint response with 8D progression tracking',
      'Standardized processes that survive shift changes and attrition',
      'Complete lot-level traceability with backward and forward search',
      'Reduced quality escapes through disciplined inspection workflows',
    ],
  },
  'tier-2': {
    title: 'Mushin for Tier 2 Manufacturers',
    subtitle: 'Build the operational foundation for growth',
    description: 'Tier 2 manufacturers are often where digital maturity is lowest but growth pressure is highest. As customer expectations increase and audit complexity rises, Mushin provides an affordable and structured entry point into digital plant control.',
    challenges: [
      'Transitioning from paper-based to digital quality records',
      'Building audit readiness without large IT investments',
      'Managing growth without proportionally adding manpower',
      'Meeting Tier 1 and OEM quality expectations',
      'Establishing traceability for the first time',
      'Standardizing operations across shifts',
    ],
    products: ['Mushin Factory OS'],
    outcomes: [
      'Affordable first rollout with Mushin Factory OS Lite',
      'Digital checksheets and inspection records from day one',
      'Structured complaint tracking and CAPA management',
      'Basic traceability with barcode and QR flows',
      'Audit-ready documentation and evidence',
      'Foundation for scaling to Pro and Enterprise tiers',
    ],
  },
  'multi-plant': {
    title: 'Mushin for Multi-Plant Groups',
    subtitle: 'Standardize operations across every plant',
    description: 'Multi-plant manufacturing groups need consistency. Different plants operating with different processes, different document versions, and different levels of discipline creates risk. Mushin provides a single platform to standardize, govern, and review operations across all plants.',
    challenges: [
      'Inconsistent processes across plants',
      'Lack of centralized visibility into plant operations',
      'Different levels of digital maturity across locations',
      'Difficulty in standardizing quality and compliance workflows',
      'Delayed or incomplete MIS from remote plants',
      'Plant-to-plant variation in audit readiness',
    ],
    products: ['Mushin Factory OS', 'Mushin Vendor Connect'],
    outcomes: [
      'Unified platform across all plants with role-based access',
      'Centralized dashboards for leadership visibility',
      'Standardized workflows, forms, and approval processes',
      'Consistent audit readiness across locations',
      'Enterprise-grade governance with multi-plant rollout support',
      'Comparative analytics across plants',
    ],
  },
  'quality': {
    title: 'Mushin for Quality & Compliance',
    subtitle: 'Control audit-critical workflows digitally',
    description: 'Quality heads and management representatives carry the weight of compliance. IATF, VDA, customer audits, process discipline: it all converges at the quality function. Mushin gives quality teams the digital tools to control, record, and retrieve evidence for every critical workflow.',
    challenges: [
      'Managing quality records across multiple formats and locations',
      'Ensuring consistency in inspection and testing workflows',
      'Complaint tracking and CAPA closure discipline',
      'Standards and document control across revisions',
      'Training and competence management',
      'Evidence retrieval under audit pressure',
    ],
    products: ['Mushin Factory OS'],
    outcomes: [
      'Complete digital quality execution: checksheets, inspections, SPC',
      'Structured 8D and CAPA workflows with approval trails',
      'Controlled document management with revision history',
      'Training matrix and competence tracking',
      'Calibration management with overdue alerts',
      'Instant evidence retrieval for any audit query',
      'Safety checklists and incident tracking',
      'ERP integration support for SAP and Oracle',
    ],
  },
  'supplier-quality': {
    title: 'Mushin for Supplier Quality',
    subtitle: 'Govern your vendor ecosystem with visibility and control',
    description: 'Supplier quality is one of the hardest areas to control. Suppliers operate outside your plant, with varying levels of capability and compliance maturity. Mushin Vendor Connect creates a structured digital layer for supplier quality management, collaboration, and governance.',
    challenges: [
      'Lack of real-time supplier quality visibility',
      'Manual supplier audit and follow-up processes',
      'Supplier complaint tracking across multiple vendors',
      'Certificate and compliance document management',
      'Supplier scorecard maintenance and rating updates',
      'Onboarding and qualification workflow inefficiency',
    ],
    products: ['Mushin Vendor Connect'],
    outcomes: [
      'Digital supplier onboarding and profile management',
      'Structured supplier audit planning and execution',
      'Real-time supplier scorecards and compliance dashboards',
      'Automated reminders for certificate renewals and follow-ups',
      'Supplier complaint tracking with CAPA linkage',
      'Complete visibility across the vendor ecosystem',
    ],
  },
  'traceability': {
    title: 'Mushin for Traceability',
    subtitle: 'Material to process to part to dispatch, fully linked',
    description: 'Traceability is no longer optional. Customer audits, recall scenarios, containment requirements all demand the ability to trace any part back to its material, process, machine, operator, and shift. Mushin provides end-to-end traceability built into every workflow.',
    challenges: [
      'Manual traceability systems that break under volume',
      'Inability to perform backward and forward trace quickly',
      'Disconnected records across incoming, production, and dispatch',
      'Barcode and QR implementation challenges',
      'Containment traceability for customer complaints',
      'Linking material, process, machine, and operator data',
    ],
    products: ['Mushin Factory OS'],
    outcomes: [
      'Lot, batch, and serial number traceability',
      'Barcode and QR-based flow integration with scanner/printer support',
      'Material-to-process-to-part linkage with bin tracking',
      'Machine, operator, and shift linkage',
      'Backward and forward trace search',
      'Containment history and dispatch trace',
    ],
  },
  'machine-data': {
    title: 'Mushin for Machine Data & IoT',
    subtitle: 'Connect machines to visibility, traceability, and control',
    description: 'Machines generate data. But without structured capture and integration, that data remains invisible. IoT & Machine Data within Mushin Factory OS bridges the gap between physical machines and digital plant control, feeding data into dashboards, traceability flows, and execution workflows.',
    challenges: [
      'Legacy machines with no digital interface',
      'Disconnected machine data and plant workflows',
      'Manual production and downtime recording',
      'No real-time visibility into machine status',
      'Process parameter data not linked to quality records',
      'Lack of OEE and performance analytics',
    ],
    products: ['Mushin Factory OS'],
    outcomes: [
      'PLC, sensor, and gateway connectivity for any machine',
      'Real-time machine status, production counts, and cycle time',
      'Downtime and breakdown analytics with MTTR/MTBF',
      'Process parameter visibility linked to quality records',
      'Andon views and alarm history',
      'OEE monitoring and performance dashboards',
      'ERP integration for production order and inspection data sync',
    ],
  },
};
